/**
 * Classification of a proactive AI notification (a notify_user-created session).
 * `general` is any ordinary notification; `bi` marks a Business-Intelligence
 * brief so the steering (BI) surface can list those separately. Ordinary user
 * chats are always `general`. This is orthogonal to the legacy `session_type`
 * (chat/statistics execution mode) and lives only on the V2 synced shape.
 */
export const CHAT_NOTIFICATION_TYPE = {
  GENERAL: "general",
  BI: "bi",
} as const

export type ChatNotificationType =
  (typeof CHAT_NOTIFICATION_TYPE)[keyof typeof CHAT_NOTIFICATION_TYPE]

export interface ChatSession {
  id: string
  userId: string
  companyId: string
  title: string | null
  /** Originating workflow execution this chat continues (AI Inbox "Reply"). */
  sourceExecutionId: string | null
  /**
   * BI dashboard a `bi` brief is ABOUT — the dashboard the originating run
   * started its analysis from. Lets the steering page attribute a brief to a
   * dashboard tab. `null` for general notifications and unattributed briefs
   * (which stay globally visible on the steering page).
   */
  sourceDashboardId: string | null
  /**
   * ALL BI dashboards a `bi` brief is about — a brief whose analysis spans
   * several dashboards is attributed to each. Wire field `source_dashboard_ids`.
   * The scalar `sourceDashboardId` is kept for back-compat (the first / primary
   * dashboard); consumers should prefer this array and fall back to the scalar
   * when it is null or empty. `null` for general notifications and unattributed
   * briefs.
   */
  sourceDashboardIds: string[] | null
  /** True when an unseen assistant reply has landed; cleared when the user reads it. */
  unread: boolean
  /**
   * Notification classification (`general` | `bi`). Set at creation by the
   * notify pipeline; ordinary chats default to `general`. Drives BI-only
   * surfacing on the steering page.
   */
  notificationType: ChatNotificationType
  syncId: number
  createdAt: string
  updatedAt: string
  deletedAt: string | null
}
